import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  createGroup,
  createGroupFailure,
  createGroupSuccess,
  deleteGroup,
  deleteGroupFailure,
  deleteGroupSuccess,
  editGroup,
  editGroupFailure,
  editGroupSuccess,
  fetchGroup,
  fetchGroupFailure,
  fetchGroupSuccess,
  fetchGroups,
  fetchGroupsFailure,
  fetchGroupsSuccess,
} from 'redux/slice/advanced-planner/AdvancedPlanner.slice';
import {
  ICreateGroupRequest,
  IDeleteGroupRequest,
  IEditGroupRequest,
  IGetGroupRequest,
  IGetGroupResponse,
  IGetGroupsRequest,
  IGetGroupsResponse,
} from 'redux/types/AdvancedPlanner.types';
import { advancedPlannerEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

// Saga for fetching all groups
function* fetchGroupsSaga(action: PayloadAction<IGetGroupsRequest>) {
  const { allocationId } = action.payload;

  try {
    const response: AxiosResponse<IGetGroupsResponse> = yield call(
      HTTPClient.GetRequest,
      advancedPlannerEndPoints.GET_GROUPS(allocationId)
    );

    yield put(fetchGroupsSuccess(response.data));
    message.success('Get groups successful');
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to fetch groups';
    yield put(fetchGroupsFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Saga for fetching a single group
function* fetchGroupSaga(action: PayloadAction<IGetGroupRequest>) {
  const { groupId } = action.payload;

  try {
    const response: AxiosResponse<IGetGroupResponse> = yield call(
      HTTPClient.GetRequest,
      advancedPlannerEndPoints.GET_GROUP(groupId)
    );

    yield put(fetchGroupSuccess(response.data));
    message.success('Get group successful');
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to fetch group';
    yield put(fetchGroupFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Saga for creating a group
function* createGroupSaga(action: PayloadAction<ICreateGroupRequest>) {
  try {
    const response: AxiosResponse<IGetGroupResponse> = yield call(
      HTTPClient.PostRequest,
      advancedPlannerEndPoints.CREATE_GROUP(),
      action.payload
    );

    yield put(createGroupSuccess(response.data));
    message.success('Group created successfully');
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to create group';
    yield put(createGroupFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Saga for editing a group
function* editGroupSaga(action: PayloadAction<IEditGroupRequest>) {
  const { groupId, ...groupData } = action.payload;
  try {
    const response: AxiosResponse<IGetGroupResponse> = yield call(
      HTTPClient.PutRequest,
      advancedPlannerEndPoints.EDIT_GROUP(groupId),
      groupData
    );

    yield put(editGroupSuccess(response.data));
    message.success('Group edited successfully');
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to edit group';
    yield put(editGroupFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Saga for deleting a group
function* deleteGroupSaga(action: PayloadAction<IDeleteGroupRequest>) {
  const { groupId } = action.payload;

  try {
    yield call(
      HTTPClient.DeleteRequest,
      advancedPlannerEndPoints.DELETE_GROUP(groupId)
    );
    yield put(deleteGroupSuccess(groupId));
    message.success('Group deleted successfully');
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to delete group';
    yield put(deleteGroupFailure(errorMessage));
    message.error(errorMessage);
  }
}

// Watchers
export function* watchAdvancedPlannerSagas() {
  yield takeLatest(fetchGroups.type, fetchGroupsSaga);
  yield takeLatest(fetchGroup.type, fetchGroupSaga);
  yield takeLatest(createGroup.type, createGroupSaga);
  yield takeLatest(editGroup.type, editGroupSaga);
  yield takeLatest(deleteGroup.type, deleteGroupSaga);
}
